import { createContext } from "react";
import { openChatCitation, type ChatCitationTarget } from "./openChatCitation";

/**
 * Where a citation chip gets the passage its entry left out (#12081).
 *
 * An agent answer's history read carries a citation's quoted passage only
 * where rendering reads it; the rest come from a per-answer request. The page
 * that knows how to make that request provides this, keyed by the bracketed
 * marker. Chat provides nothing: its entries always carry their passages.
 */
export interface CitationPassages {
  /** The marker's passage, sharing a request already in flight. */
  passageFor: (marker: string) => Promise<string | undefined>;
  /** Start fetching the marker's passage, if it is not on its way. */
  prefetch: (marker: string) => void;
}

export const CitationPassagesContext = createContext<CitationPassages | null>(
  null
);

/**
 * Whether opening this target would want a passage it does not have: only a
 * document citation highlights its passage, and every other kind of target
 * wins over the document branch in `openChatCitation`.
 */
export const needsPassage = (
  target: Pick<
    ChatCitationTarget,
    | "docId"
    | "publicId"
    | "kind"
    | "interactionId"
    | "artifactId"
    | "webUrl"
    | "iodlSource"
    | "sourceContent"
  >
): boolean =>
  !target.sourceContent &&
  Boolean(target.docId) &&
  !target.iodlSource &&
  !(target.publicId && target.kind) &&
  !target.interactionId &&
  !target.artifactId &&
  !target.webUrl;

/**
 * Open a citation, first fetching its passage when one is offered. A fetch
 * that fails opens the same document and page without the passage: the open
 * never waits on a request that cannot answer.
 */
export const openChatCitationWithPassage = async (
  target: ChatCitationTarget,
  passage?: () => Promise<string | undefined>
): Promise<void> => {
  if (!passage) {
    openChatCitation(target);
    return;
  }
  let sourceContent: string | undefined;
  try {
    sourceContent = await passage();
  } catch (error) {
    console.error("Citation passage fetch failed; opening without it", error);
  }
  openChatCitation({ ...target, sourceContent });
};
